import { FC, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IConnectStepProps } from '../../types';
import { CHANNEL_INFO, T_KEYS } from '../../constants';
import {
  ConnectCard,
  ConnectCardHeader,
  ConnectCardLogo,
  ConnectCardTitle,
  ConnectCardDescription,
  TermsCheckbox,
  ButtonsRow,
  PrimaryButton,
  SecondaryButton,
} from '../../styles';

// Import channel SVGs
import AIRBNB from '@assets/svg/airbnb.svg?react';
import GATHERN from '@assets/svg/gathren.svg?react';
import BookingIcon from '@assets/svg/BookingLogo.svg?react';

const ConnectStep: FC<IConnectStepProps> = memo(({
  channelName,
  onCancel,
  onConnect,
  isLoading,
}) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [termsAccepted, setTermsAccepted] = useState(false);

  const channelInfo = CHANNEL_INFO[channelName];
  const displayName = isRtl ? channelInfo.displayNameAr : channelInfo.displayName;

  const getChannelLogo = () => {
    switch (channelName) {
      case 'airbnb':
        return <AIRBNB />;
      case 'booking':
        return <BookingIcon />;
      case 'gathern':
        return <GATHERN />;
      default:
        return null;
    }
  };

  const getDescription = () => {
    const descriptionKeys: Record<string, string> = {
      airbnb: T_KEYS.AIRBNB_CONNECT_DESC,
      booking: T_KEYS.BOOKING_CONNECT_DESC,
      gathern: T_KEYS.GATHERN_CONNECT_DESC,
    };
    const key = descriptionKeys[channelName];
    return key ? t(key, { channel: displayName }) : '';
  };

  return (
    <ConnectCard>
      <ConnectCardHeader>
        <ConnectCardLogo>
          {getChannelLogo()}
        </ConnectCardLogo>
        <ConnectCardTitle>
          {t(T_KEYS.CONNECT_CHANNEL_WITH_MSOOL, { channel: displayName })}
        </ConnectCardTitle>
      </ConnectCardHeader>

      <ConnectCardDescription style={{ whiteSpace: 'pre-line' }}>
        {getDescription()}
      </ConnectCardDescription>

      <TermsCheckbox>
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
        />
        <span>
          {t(T_KEYS.I_AGREE_TO)}
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            {t(T_KEYS.TERMS_OF_SERVICE)}
          </a>
          {t(T_KEYS.AND)}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            {t(T_KEYS.PRIVACY_POLICY)}
          </a>
          {t(T_KEYS.OF_MSOOL)}
        </span>
      </TermsCheckbox>

      <ButtonsRow>
        <SecondaryButton onClick={onCancel}>
          {t(T_KEYS.CANCEL)}
        </SecondaryButton>
        <PrimaryButton
          $disabled={!termsAccepted || isLoading}
          onClick={onConnect}
          disabled={!termsAccepted || isLoading}
        >
          {isLoading ? t(T_KEYS.CONNECTING_LOADING) : t(T_KEYS.CONNECT)}
        </PrimaryButton>
      </ButtonsRow>
    </ConnectCard>
  );
});

ConnectStep.displayName = 'ConnectStep';

export default ConnectStep;
